import React, { useState, useEffect } from 'react';
import { ExpenseWithDetails, CreateExpenseRequest, UpdateExpenseRequest } from './types';
import { useExpenses } from './hooks/useExpenses';
import { DatePicker, CurrencyInputField } from '../../../components/common';
import { PropertyWithDetails, PropertySearchSection } from '../properties';
import { propertiesApi } from '../properties/services/propertyApi';
import ContactSearchSection from '../contacts/ContactSearchSection';
import ExpenseVendorContactSection from './ExpenseVendorContactSection';
import { ContactWithDetails } from '../contacts/types';
import { contactsApi } from '../contacts/services/contactApi';
import { CategoryDropdown } from '../category';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { useDirtyChangeReport } from '../../../hooks/useDirtyChangeReport';
import '../../../components/common/CRMForm.css';

interface ExpenseFormProps {
  expense: ExpenseWithDetails | null;
  onSave: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

interface FormErrors {
  vendorContactId?: string;
  vendorContactTypeId?: string;
  propertyId?: string;
  expenseDate?: string;
  amount?: string;
  description?: string;
}

const MIN_PROPERTIES = 1;
const MAX_PROPERTIES = 5;

const ExpenseForm: React.FC<ExpenseFormProps> = ({ expense, onSave, onCancel, onDirtyChange }) => {
  const isEditing = !!expense;
  
  // Hooks
  const { createExpense, updateExpense, deleteExpense } = useExpenses();
  
  // Form state
  const [formData, setFormData] = useState({
    vendorContactId: 0,
    propertyId: 0,
    contactId: 0,
    taxTypeId: 0,
    expenseDate: '',
    amount: 0,
    description: ''
  });

  const [selectedVendorIds, setSelectedVendorIds] = useState<number[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<ContactWithDetails | null>(null);
  const [newVendorBusinessName, setNewVendorBusinessName] = useState('');
  const [newVendorTypeId, setNewVendorTypeId] = useState(0);
  const [isNewVendorMode, setIsNewVendorMode] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<PropertyWithDetails | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactWithDetails | null>(null);
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);





  // Initialize form data
  useEffect(() => {
    if (expense) {
      setFormData({
        vendorContactId: expense.vendorContactId,
        propertyId: expense.propertyId || 0,
        contactId: expense.contactId || 0,
        taxTypeId: expense.taxTypeId || 0,
        expenseDate: expense.expenseDate,
        amount: expense.amount,
        description: expense.description || ''
      });

      // Set selected entities
      setSelectedVendorIds(expense.vendorContactId ? [expense.vendorContactId] : []);

      // Fetch property for single selection
      if (expense.propertyId) {
        propertiesApi.getById(expense.propertyId)
          .then(property => setSelectedProperty(property))
          .catch(error => {
            console.error('Error fetching property:', error);
            setSelectedProperty(null);
          });
      } else {
        setSelectedProperty(null);
      }

      // Fetch contact for single selection
      if (expense.contactId) {
        contactsApi.getById(expense.contactId)
          .then(contact => setSelectedContact(contact))
          .catch(error => {
            console.error('Error fetching contact:', error);
            setSelectedContact(null);
          });
      } else {
        setSelectedContact(null);
      }
    } else {
      // Reset for new expense
      setFormData({
        vendorContactId: 0,
        propertyId: 0,
        contactId: 0,
        taxTypeId: 0,
        expenseDate: '',
        amount: 0,
        description: ''
      });
      
      // Reset selected entities
      setSelectedVendorIds([]);
      setSelectedVendor(null);
      setNewVendorBusinessName('');
      setNewVendorTypeId(0);
      setIsNewVendorMode(false);
      setSelectedPropertyIds([]);
      setSelectedProperty(null);
      setSelectedContact(null);
    }
    setHasChanges(false);
    setErrors({});
  }, [expense]);

  // Track unsaved changes
  const { confirmNavigation } = useUnsavedChanges({ hasChanges });
  useDirtyChangeReport(hasChanges, onDirtyChange);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (isEditing) {
      if (!formData.vendorContactId) {
        newErrors.vendorContactId = 'Vendor is required';
      }
    } else if (isNewVendorMode) {
      if (!newVendorBusinessName.trim()) {
        newErrors.vendorContactId = 'Vendor business name is required';
      }
      if (!newVendorTypeId) {
        newErrors.vendorContactTypeId = 'Contact type is required';
      }
    } else if (!formData.vendorContactId) {
      newErrors.vendorContactId = 'Vendor is required';
    }

    if (!formData.expenseDate) {
      newErrors.expenseDate = 'Date is required';
    }

    if (!formData.amount || formData.amount < 0.01) {
      newErrors.amount = 'Amount must be at least $0.01';
    }

    if (!isEditing && selectedPropertyIds.length < MIN_PROPERTIES) {
      newErrors.propertyId = 'At least one property is required';
    }

    if (formData.description && formData.description.length > 200) {
      newErrors.description = 'Description must be 200 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleVendorChange = async (contactIds: number[]) => {
    const vendorContactId = contactIds[0] || 0;
    setSelectedVendorIds(contactIds);
    setErrors((prev) => ({ ...prev, vendorContactId: undefined }));

    if (!vendorContactId) {
      setFormData((prev) => ({
        ...prev,
        vendorContactId: 0,
        taxTypeId: 0,
      }));
      setHasChanges(true);
      return;
    }

    try {
      const vendor = await contactsApi.getById(vendorContactId);
      const taxTypeId = vendor.defaultCategoryId ? Number(vendor.defaultCategoryId) : 0;
      setFormData((prev) => ({
        ...prev,
        vendorContactId,
        taxTypeId,
      }));
    } catch (error) {
      console.error('Error loading vendor for default expense category:', error);
      setFormData((prev) => ({
        ...prev,
        vendorContactId,
      }));
    }
    setHasChanges(true);
  };

  const handlePropertySelect = (property: PropertyWithDetails | null) => {
    setSelectedProperty(property);
    handleInputChange('propertyId', property?.id || 0);
  };

  const handlePropertiesChange = (propertyIds: number[]) => {
    setSelectedPropertyIds(propertyIds);
    setHasChanges(true);
    if (errors.propertyId) {
      setErrors(prev => ({ ...prev, propertyId: undefined }));
    }
  };

  const handleVendorSelect = (contact: ContactWithDetails | null) => {
    setSelectedVendor(contact);
    setErrors((prev) => ({ ...prev, vendorContactId: undefined, vendorContactTypeId: undefined }));

    if (!contact) {
      setSelectedVendorIds([]);
      setFormData((prev) => ({
        ...prev,
        vendorContactId: 0,
        taxTypeId: 0,
      }));
      setHasChanges(true);
      return;
    }

    setSelectedVendorIds([contact.id]);
    const taxTypeId = contact.defaultCategoryId ? Number(contact.defaultCategoryId) : 0;
    setFormData((prev) => ({
      ...prev,
      vendorContactId: contact.id,
      taxTypeId,
    }));
    setHasChanges(true);
  };

  const handleNewVendorBusinessNameChange = (value: string) => {
    setNewVendorBusinessName(value);
    setHasChanges(true);
    if (errors.vendorContactId) {
      setErrors(prev => ({ ...prev, vendorContactId: undefined }));
    }
  };

  const handleNewVendorTypeIdChange = (value: number) => {
    setNewVendorTypeId(value);
    setHasChanges(true);
    if (errors.vendorContactTypeId) {
      setErrors(prev => ({ ...prev, vendorContactTypeId: undefined }));
    }
  };

  const handleNewVendorModeChange = (active: boolean) => {
    setIsNewVendorMode(active);
    if (active) {
      setSelectedVendor(null);
      setSelectedVendorIds([]);
      setFormData((prev) => ({
        ...prev,
        vendorContactId: 0,
        taxTypeId: 0,
      }));
    }
  };

  const handleContactSelect = (contact: ContactWithDetails | null) => {
    setSelectedContact(contact);
    handleInputChange('contactId', contact?.id || 0);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (isEditing && expense) {
        const updateData: UpdateExpenseRequest = {
          id: expense.id,
          vendorContactId: formData.vendorContactId,
          propertyId: formData.propertyId || undefined,
          contactId: formData.contactId || undefined,
          taxTypeId: formData.taxTypeId || undefined,
          expenseDate: formData.expenseDate,
          amount: formData.amount,
          description: formData.description || undefined
        };
        await updateExpense(updateData);
      } else {
        let vendorContactId = formData.vendorContactId;

        if (isNewVendorMode && newVendorBusinessName.trim() && newVendorTypeId > 0) {
          const createdVendor = await contactsApi.create({
            typeId: newVendorTypeId,
            businessName: newVendorBusinessName.trim(),
            firstName: '',
            lastName: '',
            notes: ''
          });
          vendorContactId = createdVendor.id;
        }

        const createDataBase: CreateExpenseRequest = {
          vendorContactId,
          contactId: selectedContact?.id || undefined,
          taxTypeId: formData.taxTypeId || undefined,
          expenseDate: formData.expenseDate,
          amount: formData.amount,
          description: formData.description || undefined
        };

        for (const propertyId of selectedPropertyIds) {
          await createExpense({ ...createDataBase, propertyId });
        }
      }
      
      setHasChanges(false);
      onSave();
    } catch (error) {
      console.error('Error saving expense:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!expense) return;
    
    if (window.confirm('Are you sure you want to delete this expense?')) {
      setLoading(true);
      try {
        await deleteExpense(expense.id);
        onSave();
      } catch (error) {
        console.error('Error deleting expense:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    if (!confirmNavigation()) {
      return;
    }
    onCancel();
  };

  return (
    <div className="form">
      <div className="form-header">
        <h2>{isEditing ? 'Edit Expense' : 'Create Expense'}</h2>
        {hasChanges && <span className="form-unsaved-indicator">• Unsaved changes</span>}
      </div>

      <div className="form-content">
        <div className="form-section">
          <h3>Expense Information</h3>
          <div className="form-section-content">
            <div className="app-form-group">
            {isEditing ? (
              <ContactSearchSection
                selectedContactIds={selectedVendorIds}
                onContactsChange={handleVendorChange}
                label="Vendor"
                placeholder="Search vendors by first name, last name, or business name..."
                maxSelections={1}
              />
            ) : (
              <ExpenseVendorContactSection
                selectedContact={selectedVendor}
                onContactSelect={handleVendorSelect}
                newContactBusinessName={newVendorBusinessName}
                newContactTypeId={newVendorTypeId}
                onNewContactBusinessNameChange={handleNewVendorBusinessNameChange}
                onNewContactTypeIdChange={handleNewVendorTypeIdChange}
                onNewContactModeChange={handleNewVendorModeChange}
                label="Vendor"
                placeholder="Search vendors by first name, last name, or business name..."
                errors={{ typeId: errors.vendorContactTypeId }}
              />
            )}
            {errors.vendorContactId && (
              <div className="app-error-message">{errors.vendorContactId}</div>
            )}
          </div>

          <div className="app-form-group">
            <DatePicker
              value={formData.expenseDate}
              onChange={(date) => handleInputChange('expenseDate', date)}
              placeholder="mm/dd/yyyy"
              label="Date of Payment"
              required={true}
            />
            {errors.expenseDate && (
              <div className="app-error-message">{errors.expenseDate}</div>
            )}
          </div>

          <div className="app-form-group">
            <label htmlFor="amount" className="required">
              Amount of Payment
            </label>
            <CurrencyInputField
              value={formData.amount}
              onChange={(amount) => handleInputChange('amount', amount)}
              min={0.01}
              max={9999999.99}
              placeholder="Enter an amount greater than $0.00"
            />
            {errors.amount && (
              <div className="app-error-message">{errors.amount}</div>
            )}
          </div>

          <div className="app-form-group">
            {isEditing ? (
              <PropertySearchSection
                selectedProperty={selectedProperty}
                onPropertySelect={handlePropertySelect}
                placeholder="Search properties by name or address..."
                label="Property"
              />
            ) : (
              <>
                <PropertySearchSection
                  selectedPropertyIds={selectedPropertyIds}
                  onPropertiesChange={handlePropertiesChange}
                  placeholder="Search properties by name or address..."
                  label="Property"
                  maxSelections={MAX_PROPERTIES}
                  minSelections={MIN_PROPERTIES}
                />
                {errors.propertyId && (
                  <div className="app-error-message">{errors.propertyId}</div>
                )}
              </>
            )}
          </div>

          <div className="app-form-group">
            <ContactSearchSection
              selectedContact={selectedContact}
              onContactSelect={handleContactSelect}
              label="Contact for Payment"
              placeholder="Search contacts by first name or last name..."
            />
          </div>

          <div className="app-form-group">
            <label htmlFor="taxTypeId">
              Expense category
            </label>
            <CategoryDropdown
              parentType="expense"
              value={formData.taxTypeId}
              onChange={(categoryId) => handleInputChange('taxTypeId', categoryId)}
              placeholder="Select expense category (optional)"
            />
          </div>

          <div className="app-form-group">
            <label htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter expense description (optional)"
              maxLength={200}
              rows={3}
              className="app-form-input"
            />
            {errors.description && (
              <div className="app-error-message">{errors.description}</div>
            )}
          </div>
          </div>
        </div>


      </div>

      <div className="app-form-actions">
        {isEditing && (
          <button
            type="button"
            className="app-btn app-btn-danger"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </button>
        )}
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="app-btn app-btn-primary"
          onClick={handleSave}
          disabled={loading || !hasChanges}
        >
          {loading ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
        </button>
      </div>
    </div>
  );
};

export default ExpenseForm;
